import { swedishFileTimestamp } from '../../shared/swedish-time';
import { ChangeDetectorRef, Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil, catchError, map, tap } from 'rxjs/operators';
import { ROUTE_ANIMATIONS_ELEMENTS } from '@app/core';
import { CompanyService } from '@app/core/services/CompanyService';
import { UserService } from '@app/core/services/UserService';
import { InvoiceService } from '@app/core/services/InvoiceService';
import { DataSource } from '@angular/cdk/collections';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { EditCustomerDialogComponent } from '../../shared/customer/EditCustomerDialog.Component';
import { ICompany } from '@app/core/interfaces/ICompany';
import { MatDatepicker, MatDatepickerToggle } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { IAccountSettings } from '@app/core/interfaces/user/IAccountSettings';
import { Router, ActivatedRoute, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { EditAccountDialogComponent } from '../../shared/account/EditAccountDialog.Component';
import { UserEditComponent } from './edit-user/user-edit.component';
import { UserQueryParams } from '@app/core/interfaces/IUserQueryParams';
import { PagedResult } from '@app/core/interfaces/IPagedResult';
import { ApplicationUserVM } from '@app/core/interfaces/IApplicationUserVM';
import { PageEvent } from '@angular/material/paginator';
import { debounceTime } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import { AuthenticationService } from '@app/core/services/AuthenticationService';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/ConfirmDialog';
import { IConfirmResponse } from '@app/core/interfaces/confirm/IConfirmResponse';

@Component({
  standalone: false,
  selector: 'users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss']
})
export class UsersComponent implements OnInit, OnDestroy {

  public isParentAccount: boolean = false;
  public isCompanyUser: boolean = false;
  public isAdmin: boolean = false;
  private unsubscribe$: Subject<void> = new Subject<void>();
  routeAnimationsElements = ROUTE_ANIMATIONS_ELEMENTS;
  displayedColumns = ['name', 'memberNumber', 'personalNumber', 'email', 'created', 'lastLogin', 'suspended', 'actions'];
  customerData: Array<any>;
  sortedData;
  downloadingReport: boolean = false;
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  dataSource = new MatTableDataSource<ApplicationUserVM>();
  isLoading = true;
  totalCount = 0;
  pageSize = 5;
  pageIndex = 0;
  sortBy = 'FirstName';
  sortDescending = false;
  searchTerm = '';
  searchControl = new UntypedFormControl();
  statusFilter: 'all' | 'active' | 'suspended' = 'all';
  allCount = 0;
  activeCount = 0;
  suspendedCount = 0;
  
  constructor(public store: Store<any>,
      public snackBar: MatSnackBar,
      private companyService: CompanyService,
      private userService: UserService,
      private invoiceService: InvoiceService,
      private authenticationService: AuthenticationService,
      private router: Router,
      private loadingService: LoadingBarService,
      private changeDetectorRef: ChangeDetectorRef,
      public dialog: MatDialog) {
  }

  ngOnInit() {

    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.unsubscribe$))
      .subscribe(value => {
        this.applyFilter(value);
      });

    this.loadingService.start();
    this.isAdmin = this.userService.userHasRole('admin');
    this.isParentAccount = this.userService.userIsParentAccount();
    this.isCompanyUser = this.userService.userHasRole('companyuser');
    this.getUsers();
  }

  applyFilter(value: string): void {
    this.searchTerm = value.trim();
    this.pageIndex = 0;
    this.getUsers();
  }
  
  getExcelReport(): void {
    if (this.downloadingReport) {
      return;
    }

    this.downloadingReport = true;
    this.changeDetectorRef.detectChanges();
    const progressSnackBar = this.snackBar.open('Skapar medlemslista...');

    this.companyService.GetCompanyUsersFile(1)
      .subscribe(blob => {
        if (!blob || blob.size === 0) {
          progressSnackBar.dismiss();
          this.snackBar.open('Medlemslistan kunde inte hämtas.', undefined, { duration: 5000 });
          this.downloadingReport = false;
          this.changeDetectorRef.detectChanges();
          return;
        }

        // Skapa en timestamp för att unikt namnge filen
        const timestamp = swedishFileTimestamp();
        const filename = `Medlemslista_${timestamp}.xlsx`;

        // Hantering för Internet Explorer
        if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
          (window.navigator as any).msSaveOrOpenBlob(blob, filename);
          progressSnackBar.dismiss();
          this.downloadingReport = false;
          this.changeDetectorRef.detectChanges();
          return;
        }

        // Skapa en nedladdningslänk
        const data = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = data;
        link.download = filename;

        document.body.appendChild(link);
        link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));

        setTimeout(() => {
          window.URL.revokeObjectURL(data);
          link.remove();
          progressSnackBar.dismiss();
          this.downloadingReport = false;
          this.changeDetectorRef.detectChanges();
        }, 100);
      }, error => {
        console.error("Fel vid hämtning av rapport:", error);
        progressSnackBar.dismiss();
        this.snackBar.open('Medlemslistan kunde inte hämtas.', undefined, { duration: 5000 });
        this.downloadingReport = false;
        this.changeDetectorRef.detectChanges();
      });
  }

  compare(a, b, isAsc) {
      console.log((a < b ? -1 : 1) * (isAsc ? 1 : -1));
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
  
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getUsers(): void {
    this.isLoading = true;

    const params: UserQueryParams = {
      pageNumber: this.pageIndex + 1, // API förväntar sig 1-baserad index
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      sortDescending: this.sortDescending,
      searchTerm: this.searchTerm,
      suspended: this.statusFilter === 'all' ? undefined : this.statusFilter === 'suspended'
    };

    this.userService.getUsers(params)
      .subscribe(
        result => this.onUsersRetrieved(result),
        error => {
          this.completeLoading();
          console.error('Fel vid hämtning av användare:', error);
        }
      );
  }

  sortData(event: Sort): void {
    this.sortBy = event.active;
    this.sortDescending = event.direction === 'desc';
    this.pageIndex = 0; // börja om från första sidan
    this.getUsers();
  }

  addInvoice(user: any)
  {    
      this.invoiceService.createInvoice({ User: { Id: user.Id } }, 'Fakturan skapades!', undefined)
          .subscribe(value => this.onInvoiceCreated(value),
          error => console.log(error)
          );
  }

  addJobPreNotification(user: any): void {
    const payload = {
      User: { Id: user.Id },
      Status: { TechnicalName: 'Draft', Name: 'Utkast' },
      IsJobPreNotificationSave: true,
      WorkNoticeStatus: { TechnicalName: 'PreNotified' }
    };

    this.invoiceService.createInvoice(payload, 'Föranmälan skapades!', undefined).subscribe(
      value => this.onJobPreNotificationCreated(value),
      error => console.log(error)
    );
  }

  delete(user: any) {
      var result = confirm("Vill du radera " + user.FirstName + " " + user.LastName + "?");
      if (result) {
          this.userService.deleteUser(user.Id)
              .subscribe(value => this.onInvoiceDeleted(user.Id),
              error => console.log(error)
              );
      }
      
  }

  onInvoiceDeleted(id: number)
  {
      
      for (var i = 0; i < this.customerData.length; i++)
      {
          if (this.customerData[i].Id == id)
          {
              this.customerData.splice(i, 1);
          }
      }

      this.dataSource = new MatTableDataSource(this.customerData);
  }
    
  onInvoiceCreated(response: any)
  {
      this.router.navigate(['/invoice-tool', response.Id]);
      console.log(response);
  }

  onJobPreNotificationCreated(response: any): void {
    if (response && response.Id) {
      this.router.navigate(['/job-pre-notification'], { queryParams: { invoiceId: response.Id } });
    }
  }

  onUsersRetrieved(result: PagedResult<ApplicationUserVM>): void {
    this.dataSource.data = result.Items;
    this.totalCount = result.TotalCount;
    this.allCount = result.AllCount != null ? result.AllCount : result.TotalCount;
    this.activeCount = result.ActiveCount != null ? result.ActiveCount : 0;
    this.suspendedCount = result.SuspendedCount != null ? result.SuspendedCount : 0;
    this.completeLoading();
  }

  private completeLoading(): void {
    setTimeout(() => {
      this.isLoading = false;
      this.loadingService.complete();
      this.changeDetectorRef.detectChanges();
    });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.getUsers();
  }

  clearSearch(): void {
    this.searchControl.setValue('');
  }

  setStatusFilter(filter: 'all' | 'active' | 'suspended'): void {
    if (this.statusFilter === filter) {
      return;
    }

    this.statusFilter = filter;
    this.pageIndex = 0;
    this.getUsers();
  }

  resetFilters(): void {
    this.statusFilter = 'all';
    this.pageIndex = 0;
    if (this.searchControl.value) {
      this.searchControl.setValue('');
      return;
    }

    this.searchTerm = '';
    this.getUsers();
  }

  getInitials(user: any): string {
    const firstName = String(user && user.FirstName || '').trim();
    const lastName = String(user && user.LastName || '').trim();
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
  }

  getAvatarTone(user: any): number {
    const value = `${user && user.FirstName || ''}${user && user.LastName || ''}`;
    return Array.from(value).reduce((sum, character) => sum + character.charCodeAt(0), 0) % 5;
  }

  canImpersonate(user: any): boolean {
    if (!this.isAdmin || !user || user.Suspended || this.getUserId(user) === this.authenticationService.currentUserUserId()) {
      return false;
    }

    return !Array.isArray(user.Roles) || !user.Roles.some(role =>
      String(role?.Name || '').toLowerCase() === 'admin');
  }

  confirmImpersonation(user: any): void {
    const targetUserId = this.getUserId(user);
    if (!targetUserId || !this.canImpersonate(user)) {
      return;
    }

    const displayName = [user.FirstName, user.LastName].filter(value => !!value).join(' ');
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      data: {
        headline: 'Logga in som användare',
        text: `Du kommer att loggas in som <strong>${this.escapeHtml(displayName)}</strong><br>${this.escapeHtml(user.Email || '')}. Sessionen avslutas automatiskt efter 30 minuter.`,
        hideInput: true,
        requireInput: false,
        confirmButtonText: 'Logga in som användaren',
        confirmButtonIcon: 'login',
        confirmButtonColor: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe((result: IConfirmResponse) => {
      if (!result?.Confirmed) {
        return;
      }

      this.loadingService.start();
      this.authenticationService.startImpersonation(targetUserId).subscribe({
        next: () => {
          this.loadingService.complete();
          this.router.navigate(['/about']);
        },
        error: error => {
          this.loadingService.complete();
          const message = error?.error?.detail || 'Det gick inte att logga in som användaren.';
          this.snackBar.open(message, '', { duration: 7000 });
        }
      });
    });
  }


  edit(user: any) {
    var newWindow = window.open("#/users/" + user.Id);
  }

  openUserPage(user: any, event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }

    const userId = this.getUserId(user);
    if (!userId) {
      console.error('Missing user id for edit modal', user);
      return;
    }
    const dialogRef = this.dialog.open(UserEditComponent, {
      width: '1040px',
      maxWidth: 'calc(100vw - 48px)',
      height: 'calc(100vh - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      panelClass: 'user-admin-dialog-panel',
      data: {
        userId: userId
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.getUsers();
    });
  }

  createNewUser(user?: any) {

    const dialogRef = this.dialog.open(EditAccountDialogComponent, {
      width: '350px',
      height: '440px',
      data: {
        id: 0
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      this.getUsers();
    });

  }

  private getUserId(user: any): string | null {
    if (!user) {
      return null;
    }

    return user.Id || user.id || user.Guid || user.guid
      || (user.User && (user.User.Guid || user.User.Id))
      || null;
  }

  copyEmail(email?: string): void {
    const trimmedEmail = (email || '').trim();
    if (!trimmedEmail) {
      return;
    }

    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(trimmedEmail)
        .then(() => this.snackBar.open('E-postadress kopierad', '', { duration: 2000 }))
        .catch(() => this.copyEmailFallback(trimmedEmail));
    } else {
      this.copyEmailFallback(trimmedEmail);
    }
  }

  private copyEmailFallback(value: string): void {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (successful) {
        this.snackBar.open('E-postadress kopierad', '', { duration: 2000 });
      } else {
        throw new Error('execCommand failed');
      }
    } catch {
      this.snackBar.open('Kunde inte kopiera e-post', '', { duration: 2000 });
    }
  }

  private escapeHtml(value: string): string {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

}





